import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Avatar,
  HStack,
  Image,
  Select,
  Spinner,
  IconButton,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";
import CheckMark from "@root/assets/icons/checkMark.svg";
import { DataTable } from "@root/components/datatable";
import { ColumnDef } from "@tanstack/react-table";
import { useJournalistContext } from "@root/context/journalist-context";
import { formatDateToISO, formatDate } from "@root/utils/date-helper";
import SearchInput from "../common/SearchInput";
import { ModalDelete } from "@root/components/modal-delete";
import { OrganizationService } from "@root/services";
import { PAGINATION_DEFAULTS, USER_TYPES } from "@root/constants";
import CustomSelect from "@root/components/common/CustomSelect";
import { ChangeHistoryIcon } from "@root/assets/icons/historyIcon";
import { useGlobalContext } from "@root/context/global-context";
import { ProfileChangeHistoryDrawer } from "../common/drawer/ProfileChangeHistoryDrawer";
import { useSelector } from "react-redux";
import { getLoggedInUser } from "@root/store/selectors/entities";

const AdminJournalistManagment = () => {
  const { t } = useTranslation("common");
  const {
    deleteJournalistManagement,
    aLlJournalistsData,
    fetchALlJournalists,
    aLlJournalistsLoading,
  } = useJournalistContext();

  const [searchValue, setSearchValue] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });
  const [organizationOptions, setOrganizationOptions] = useState<
    { id: string; companyName: string }[]
  >([]);
  const [organization, setOrganization] = useState("");
  const [page, setPage] = useState(1);
  const { getChatHistory, userHistory, historyPage } = useGlobalContext();
  const user: any = useSelector(getLoggedInUser);
  const [isViewHistoryOpen, setIsViewHistoryOpen] = useState(false);
  const fetchJournalistsWithPagination = useCallback(
    (newPage: number = 1, search: string = "", org: string = "") => {
      const payload = {
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        offset: (newPage - 1) * PAGINATION_DEFAULTS.PAGESIZE,
        search: search.trim(),
        subscriberId: org,
      };

      fetchALlJournalists(payload);
    },
    [fetchALlJournalists]
  );

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const orgService = new OrganizationService();
        const response = await orgService.fetchPublicCompanyNames({
          role: USER_TYPES.BOTH_SUBSCRIBER_AND_NON_SUBSCRIBER,
        });
        setOrganizationOptions(response?.data?.data || []);
      } catch (e) {
        setOrganizationOptions([]);
      }
    };
    fetchOrganizations();
  }, []);

  useEffect(() => {
    const payload = {
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      offset: (page - 1) * PAGINATION_DEFAULTS.PAGESIZE,
      search: searchValue.trim(),
      subscriberId: organization || "",
    };
    fetchALlJournalists(payload);
  }, [page, searchValue, organization]);

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setPage(1);
    fetchJournalistsWithPagination(1, value, organization);
  }, 500);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleOrganizationChange = useCallback(
    (value: string) => {
      setOrganization(value);
      setPage(1);
      fetchJournalistsWithPagination(1, searchValue, value);
    },
    [fetchJournalistsWithPagination, searchValue]
  );
  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      fetchJournalistsWithPagination(newPage, searchValue, organization);
    },
    [fetchJournalistsWithPagination, searchValue, organization]
  );

  const filteredJournalists = useMemo(() => {
    return aLlJournalistsData?.rows || [];
  }, [aLlJournalistsData?.rows]);

  const handleChangeHistoryClick = async (id: any) => {
    setSelectedUserId(id); // Store the ID
    const payload = {
      id: id,
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      offset: (historyPage - 1) * PAGINATION_DEFAULTS.PAGESIZE,
    };
    try {
      await getChatHistory(payload);
      setIsViewHistoryOpen(true);
    } catch (error) {
      // Error fetching user history
    }
  };

  const columns = useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        header: t("journalistRequests.name"),
        accessorKey: "name",
        cell: ({ row }) => (
          <HStack>
            <Avatar
              size="sm"
              name={row?.original?.fullName}
              src={row?.original?.avatar}
            />
            <Box>
              <Text fontWeight="500">{row?.original?.fullName}</Text>
              <Text fontSize="sm" color="gray.500">
                {row?.original?.phone}
              </Text>
            </Box>
          </HStack>
        ),
      },
      {
        header: t("journalistRequests.subscriber"),
        accessorKey: "email",
        cell: ({ row }) => row?.original?.subscriber?.companyName,
      },
      {
        header: t("journalistRequests.email"),
        accessorKey: "email",
        cell: ({ row }) => row?.original?.email,
      },

      {
        header: t("journalistRequests.requestDate"),
        accessorKey: "date",
        cell: ({ row }) => formatDateToISO(row?.original?.createdAt),
      },
      {
        header: t("journalistRequests.viewHistory"),
        accessorKey: "viewHistory",
        cell: ({ row }) => (
          <Flex w="50%" h="100%" justify="center" align="center">
            <IconButton
              aria-label="View History"
              icon={<ChangeHistoryIcon />}
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleChangeHistoryClick(row?.original?.id);
              }}
            />
          </Flex>
        ),
      },
      {
        header: t("journalistRequests.lastLogin"),
        accessorKey: "date",
        cell: ({ row }) =>
          formatDate(row?.original?.updatedAt, "YYYY-MM-DD hh:mm A"),
      },
    ],
    [t]
  );

  const noRequestFound = () => (
    <Flex
      flexDir={"column"}
      justifyContent={"center"}
      alignItems={"center"}
      height={"350px"}
    >
      <Image src={CheckMark} />
      <Text fontSize={"24px"} fontWeight={600} mt={3}>
        {t("journalistRequests.noRecentRequests")}
      </Text>
      {/* <Text fontSize={"16px"} fontWeight={400} mt={1}>
        {t("journalistRequests.noApprovalRequestYet")}
      </Text> */}
    </Flex>
  );

  return (
    <Box p={4} m={5}>
      <Flex justifyContent={"space-between"} mb={4} mt={"16px"}>
        <Box>
          <Text fontSize={"24px"} fontWeight={600} color={"#2F2F34"}>
            {t("journalistRequests.journalistManagement")}
          </Text>
          <Text mt={"8px"} fontSize={"16px"} fontWeight={400} color={"#798692"}>
            {t("journalistRequests.journalistAdminDescription")}
          </Text>
        </Box>
      </Flex>
      <Flex mb={6} gap={4}>
        <SearchInput
          maxWidth="600px"
          width={"500px"}
          value={searchValue}
          placeholder={t("journalistRequests.searchBy")}
          onChange={(e) => handleSearchChange(e.target.value)}
        />

        <CustomSelect
          borderRadius={"12px"}
          height={"44px"}
          mt={9}
          placement="bottom-end"
          value={organization}
          onChange={(value: string) => handleOrganizationChange(value)}
          placeholder={t("journalistRequests.subscriber")}
          options={
            organizationOptions
              ?.filter(
                (org) => org?.companyName && org?.companyName.trim() !== ""
              )
              ?.map((org) => ({
                value: org?.id,
                label: org?.companyName,
              })) || []
          }
        />
      </Flex>

      <Box maxH={"calc(100vh - 150px)"} overflowY={"auto"}>
        {aLlJournalistsLoading ? (
          <Flex
            flexDir={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            height={"350px"}
            gap={3}
          >
            <Spinner size="lg" color="#1819B4" />
          </Flex>
        ) : filteredJournalists?.length > 0 ? (
          <DataTable
            data={filteredJournalists}
            columns={columns}
            canEditRow={false}
            getRowId={(row: any) => row?.email}
            showPagination={true}
            page={page}
            pageCount={aLlJournalistsData?.count || 0}
            pageSize={PAGINATION_DEFAULTS.PAGESIZE}
            handlePageChange={handlePageChange}
          />
        ) : (
          noRequestFound()
        )}
      </Box>
      <ProfileChangeHistoryDrawer
        isOpen={isViewHistoryOpen}
        onClose={() => {
          setIsViewHistoryOpen(false);
          setSelectedUserId(null); // Clear the ID when closing
        }}
        viewHistoryChange={handleChangeHistoryClick}
        userId={selectedUserId} // Pass the ID to the drawer
      />
      <ModalDelete
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, id: null })}
        onConfirm={async () => {
          const payload = {
            limit: PAGINATION_DEFAULTS.PAGESIZE,
            offset: (page - 1) * PAGINATION_DEFAULTS.PAGESIZE,
            search: searchValue.trim(),
            subscriberId: organization
              ? organizationOptions.find(
                  (opt) => opt.companyName === organization
                )?.id
              : undefined,
          };
          await deleteJournalistManagement(deleteModal.id, payload);
          setDeleteModal({ isOpen: false, id: null });
        }}
        title={t("journalistRequests.deleteTitle")}
        description={t("journalistRequests.deleteDescription")}
        deleteButtonTitle={t("journalistRequests.delete")}
        cancelButtonTitle={t("journalistRequests.cancel")}
      />
    </Box>
  );
};

export default AdminJournalistManagment;
